"use client";

import { forwardRef, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Icon, type IconName } from "@/components/ui/icon";
import { SkeletonBox, SkeletonContainer } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

/**
 * Shared building blocks for the three step-editor insertion panels
 * (Variables / Snippets / AI Prompts). Each panel is now a standalone
 * popover surface — the search bar, tabs, skeleton, empty state and footer
 * below are reused so snippets and AI prompts match the variables panel
 * design.
 */

/** Outer popover surface shared by the snippets and AI prompts panels. */
export function PanelShell({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      data-portal-dropdown="true"
      className={cn(
        "flex w-[360px] flex-col overflow-hidden rounded-lg border border-contrast/1 bg-surface-elevated shadow-[0px_8px_24px_0px_rgba(0,0,0,0.35)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Tab strip (All / My) mirroring the standalone list-page toolbars. */
export function PanelTabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (tab: T) => void;
}) {
  return (
    <div className="px-3 pt-3 pb-2">
      <Tabs value={value} onValueChange={(v) => onChange(v as T)} size="sm">
        <TabsList>
          {tabs.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
}

interface PanelSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  onEscape?: () => void;
}

/**
 * Search input that visually matches the variables `CommandInput` (leading
 * magnifier, 48px tall, bottom border) but is a plain controlled input so the
 * surrounding panel owns filtering/scoping.
 */
export const PanelSearch = forwardRef<HTMLInputElement, PanelSearchProps>(
  function PanelSearch({ value, onChange, placeholder, onEscape }, ref) {
    return (
      <div className="flex h-12 items-center gap-2.5 border-y border-contrast/6 px-4">
        <Icon
          name="IconMagnifyingGlass"
          size={18}
          className="shrink-0 text-contrast/30"
        />
        <input
          ref={ref}
          type="text"
          className="flex h-full w-full bg-transparent text-sm text-heading outline-hidden placeholder:text-contrast/30"
          placeholder={placeholder}
          aria-label={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              onEscape?.();
            }
          }}
        />
      </div>
    );
  },
);

/** Loading placeholder that mimics the panel list rows. */
export function PanelListSkeleton({
  rows = 5,
  lines = 1,
  showLeadingIcon = false,
}: {
  rows?: number;
  lines?: number;
  showLeadingIcon?: boolean;
}) {
  return (
    <SkeletonContainer
      role="status"
      aria-label="Loading"
      className="flex-col gap-0.5 p-1.5"
    >
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center gap-2 px-2 py-1.5">
          {showLeadingIcon ? (
            <SkeletonBox className="size-8 shrink-0" />
          ) : null}
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <SkeletonBox className="h-3 w-1/2" />
            {lines > 1 ? (
              <SkeletonBox tone="muted" className="h-2.5 w-3/4" />
            ) : null}
          </div>
        </div>
      ))}
    </SkeletonContainer>
  );
}

/** Rich empty state (icon + title + description + learn-more link). */
export function PanelEmptyState({
  icon,
  title,
  description,
  linkLabel,
  onLink,
}: {
  icon: IconName;
  title: string;
  description: string;
  linkLabel?: string;
  onLink?: () => void;
}) {
  return (
    <div className="flex min-h-[180px] flex-col items-center justify-center gap-1.5 px-6 py-10 text-center">
      <Icon name={icon} size={32} className="text-contrast/30" />
      <p className="text-sm font-medium text-heading">{title}</p>
      <p className="text-xs leading-relaxed text-contrast/60">
        {description}
        {linkLabel ? (
          <>
            {" "}
            <button
              type="button"
              onClick={onLink}
              className="cursor-pointer text-primary transition-colors hover:underline"
            >
              {linkLabel}
            </button>
          </>
        ) : null}
      </p>
    </div>
  );
}

/** Compact "no results for this search" state. */
export function PanelNoResults({ label }: { label: string }) {
  return (
    <div className="flex min-h-[180px] items-center justify-center px-6 py-10 text-center text-xs text-contrast/50">
      {label}
    </div>
  );
}

/** Footer with the blue "Create new" CTA and a secondary "Configure" button. */
export function PanelFooter({
  createLabel = "Create new",
  configureLabel = "Configure",
  onCreate,
  onConfigure,
}: {
  createLabel?: string;
  configureLabel?: string;
  onCreate?: () => void;
  onConfigure?: () => void;
}) {
  if (!onCreate && !onConfigure) return null;

  return (
    <div className="flex items-center justify-between gap-2 border-t border-contrast/6 p-2">
      {onCreate ? (
        <Button
          variant="primary"
          size="sm"
          onClick={onCreate}
          icon={<Icon name="IconPlusSmall" size={16} />}
        >
          {createLabel}
        </Button>
      ) : null}
      {onConfigure ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={onConfigure}
          icon={<Icon name="IconSettingsGear1" size={16} />}
        >
          {configureLabel}
        </Button>
      ) : null}
    </div>
  );
}

/** Debounce a fast-changing value so panel searches don't refetch per keystroke. */
export function useDebouncedValue<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}
